/**
 * Shared Banked Recon Financial Model — Single Source of Truth
 *
 * Every banked financial calculation (GOS, company revenue, net profit, override, etc.)
 * must use this function. No independent formulas in components.
 *
 * Consumers:
 * - BankerRecon.tsx (live draft)
 * - BankerReconSummary.tsx (display)
 * - UserCompensation.tsx (finalized records)
 * - Email payloads
 */

import { isBuydownLineType, isBuydownPassThrough } from './buydownPassThrough';
import type { FhaUfmipBridge } from './fhaUfmip';
import { isManualAdjustmentLineType, isManualPassThroughLineType } from './manualAdjustments';
import { isGovernmentFundingPassThrough } from './wireReconciliation';

const STANDARD_FEE_IDS = ['processing_fee', 'underwriting_fee', 'interest_charged'];

// ============================================================================
// INPUT / OUTPUT TYPES
// ============================================================================

export interface BankedReconLineItem {
  category: string;
  line_type: string;
  description: string;
  amount: number;
  reimbursable?: boolean;
  nets_to_warehouse_advance?: boolean | null;
  /** Retained-at-warehouse amount excluded from P&L (carried through for wire decomposition only). */
  warehouse_retained_amount?: number | null;
}

type ReimbursementBasisInput = {
  amount?: number | string | null;
  warehouse_retained_amount?: number | string | null;
};

function toFiniteNumber(value: number | string | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function getLoanRevenueReimbursementBasis(item: ReimbursementBasisInput): number {
  return toFiniteNumber(item.amount) + toFiniteNumber(item.warehouse_retained_amount);
}

export interface BankedReconInput {
  loanAmount: number;
  actualPurchasePrice: number;          // percentage (e.g., 103.000)
  compensationType: 'flat_fee' | 'percentage' | string;
  compensationValue: number;
  /** Separate committed cash installment; never part of ordinary LO pricing profitability. */
  volumeBonusAmount?: number;
  overrides: Array<{ bps: number }>;     // empty if none
  // Encompass-sourced fees — always explicit, never in lineItems
  processingFee: number;
  underwritingFee: number;
  interestIncome: number;
  // Other explicit inputs
  servicingInterestIncome: number;
  flexcloseCredit: number;
  loanType?: string | null;
  /**
   * Canonical FHA UFMIP cash bridge. This is never represented as an editable
   * line item: the premium clears against the HUD remittance and only the
   * signed company variance affects P&L.
   */
  fhaUfmip?: FhaUfmipBridge | null;
  // Line items: sole source for warehouse/investor costs, lender credit,
  // LO deductions, non-reimbursable user fees, reimbursables
  lineItems: BankedReconLineItem[];
}

export interface CostItem {
  lineType: string;
  description: string;
  amount: number;
  netsToWarehouseAdvance?: boolean | null;
  /** Retained-at-warehouse amount excluded from P&L (carried for wire decomposition only). */
  warehouseRetainedAmount?: number | null;
  /** Amount payable/reimbursable to the LO; separates reimbursement basis from P&L `amount`. */
  reimbursementAmount?: number;
}

export interface BankedReconResult {
  // LO Compensation
  loPayoutBase: number;
  totalDeductions: number;
  loPayout: number;
  totalReimbursable: number;
  loPayoutWithReimbursables: number;
  volumeBonusAmount: number;
  totalLoCompensation: number;

  // Pricing
  actualPurchaseDollar: number;
  gosAmount: number;

  // Revenue
  processingFee: number;
  underwritingFee: number;
  nonReimbursableUserFees: number;
  companyRevenue: number;

  // Override
  totalOverrideAmount: number;

  // Interest
  interestIncome: number;
  servicingInterestIncome: number;

  // Warehouse costs
  warehouseInterest: number;
  otherWarehouseCosts: number;
  shortageFunded: number;

  // Investor costs
  investorInterestCost: number;
  otherInvestorCosts: number;
  escrowPassThrough: number;
  governmentFundingPassThrough: number;
  buydownPassThrough: number;
  fhaUfmip: FhaUfmipBridge | null;
  fhaUfmipCompanyVariance: number;

  // Lender credit
  lenderCredit: number;
  cureAppliedLenderCredit: number;
  /** Other `lender_cost` rows (e.g. the MI premium Lendwise pays the insurer): company expense outside the wire. */
  otherLenderCost: number;
  totalLenderCost: number;
  flexcloseCredit: number;

  // Display-ready item arrays
  reimbursableItems: CostItem[];
  loDeductionItems: CostItem[];
  nonReimbursableUserFeeItems: CostItem[];
  investorCostItems: CostItem[];
  warehouseCostItems: CostItem[];
  lenderCreditItems: CostItem[];
  otherLenderCostItems: CostItem[];
  /** Manual pass-throughs: received in the wire and remitted to a third party — clearing, never P&L. */
  manualPassThroughItems: CostItem[];

  // Display totals
  totalRevenueForDisplay: number;
  totalCostsForDisplay: number;

  // Final
  netProfit: number;
}

// ============================================================================
// MAIN CALCULATOR
// ============================================================================

export function computeBankedRecon(input: BankedReconInput): BankedReconResult {
  const {
    loanAmount,
    actualPurchasePrice,
    compensationType,
    compensationValue,
    volumeBonusAmount = 0,
    overrides,
    processingFee,
    underwritingFee,
    interestIncome,
    servicingInterestIncome,
    flexcloseCredit,
    loanType,
    fhaUfmip = null,
    lineItems
  } = input;

  if (!Number.isFinite(volumeBonusAmount) || volumeBonusAmount < 0) {
    throw new Error('Volume bonus must be a non-negative finite amount.');
  }

  // --- Categorize line items ---
  const warehouseCostItems: CostItem[] = [];
  const investorCostItems: CostItem[] = [];
  const loDeductionItems: CostItem[] = [];
  const reimbursableItems: CostItem[] = [];
  const nonReimbursableUserFeeItems: CostItem[] = [];
  const lenderCreditItems: CostItem[] = [];
  const otherLenderCostItems: CostItem[] = [];
  const manualPassThroughItems: CostItem[] = [];
  let lenderCredit = 0;
  let cureAppliedLenderCredit = 0;
  let otherLenderCost = 0;

  for (const li of lineItems) {
    const item: CostItem = {
      lineType: li.line_type,
      description: li.description,
      amount: li.amount,
      netsToWarehouseAdvance: li.nets_to_warehouse_advance ?? null,
      warehouseRetainedAmount: li.warehouse_retained_amount ?? null,
      reimbursementAmount: getLoanRevenueReimbursementBasis(li)
    };

    // Identified by line_type alone, like every manual adjustment: never revenue or cost.
    if (isManualPassThroughLineType(li.line_type)) {
      manualPassThroughItems.push(item);
      continue;
    }

    switch (li.category) {
      case 'warehouse_cost':
        warehouseCostItems.push(item);
        break;
      case 'investor_cost':
        investorCostItems.push(item);
        break;
      case 'lender_cost':
        if (li.line_type === 'lender_credit') {
          lenderCredit += li.amount;
          lenderCreditItems.push(item);
        } else if (li.line_type === 'cure_applied_lender_credit') {
          cureAppliedLenderCredit += li.amount;
          lenderCreditItems.push(item);
        } else {
          otherLenderCost += li.amount;
          otherLenderCostItems.push(item);
        }
        break;
      case 'lo_deduction':
        loDeductionItems.push(item);
        break;
      case 'loan_revenue':
        if (!STANDARD_FEE_IDS.includes(li.line_type)) {
          // A manual adjustment is identified by its line_type; its free-text description must never
          // reclassify it (e.g. "buydown refund").
          if (!isManualAdjustmentLineType(li.line_type) && isBuydownLineType(li.line_type, li.description)) {
            break;
          }
          if (li.reimbursable) {
            reimbursableItems.push(item);
          } else {
            nonReimbursableUserFeeItems.push(item);
          }
        }
        break;
    }
  }

  // --- LO Compensation ---
  const loPayoutBase = compensationType === 'flat_fee'
    ? compensationValue
    : loanAmount > 0 ? (loanAmount * compensationValue) / 100 : 0;

  const totalDeductions = loDeductionItems.reduce((sum, d) => sum + d.amount, 0);
  const loPayout = loPayoutBase - totalDeductions;

  const totalReimbursable = reimbursableItems.reduce((sum, r) => sum + (r.reimbursementAmount ?? r.amount), 0);
  const loPayoutWithReimbursables = loPayout + totalReimbursable;
  const totalLoCompensation = loPayoutWithReimbursables + volumeBonusAmount;

  // --- Pricing ---
  const actualPurchaseDollar = loanAmount * (actualPurchasePrice - 100) / 100;
  const gosAmount = actualPurchaseDollar - loPayout - volumeBonusAmount;

  // --- Revenue ---
  const nonReimbursableUserFees = nonReimbursableUserFeeItems.reduce((sum, f) => sum + f.amount, 0);
  const companyRevenue = gosAmount + processingFee + underwritingFee + nonReimbursableUserFees;

  // --- Override ---
  const totalOverrideAmount = loanAmount > 0
    ? overrides.reduce((sum, o) => sum + (o.bps > 0 ? (loanAmount * o.bps) / 10000 : 0), 0)
    : 0;

  // --- Warehouse costs ---
  const totalWarehouseCosts = warehouseCostItems.reduce((sum, c) => sum + c.amount, 0);
  const warehouseInterest = warehouseCostItems
    .filter(c => c.lineType === 'warehouse_interest')
    .reduce((sum, c) => sum + c.amount, 0);
  const shortageFunded = warehouseCostItems
    .filter(c => c.lineType === 'shortage_funded')
    .reduce((sum, c) => sum + c.amount, 0);
  const otherWarehouseCosts = totalWarehouseCosts - warehouseInterest - shortageFunded;

  // --- Investor costs ---
  const totalInvestorCosts = investorCostItems.reduce((sum, c) => sum + c.amount, 0);
  const investorInterestCost = investorCostItems
    .filter(c => c.lineType.startsWith('interest_cost'))
    .reduce((sum, c) => sum + c.amount, 0);
  const escrowPassThrough = investorCostItems
    .filter(c => c.lineType.startsWith('escrow_deposit'))
    .reduce((sum, c) => sum + c.amount, 0);
  const governmentFundingPassThrough = investorCostItems
    .filter(c => isGovernmentFundingPassThrough({
      category: 'investor_cost',
      line_type: c.lineType,
      description: c.description
    }, loanType))
    .reduce((sum, c) => sum + c.amount, 0);
  const buydownPassThrough = investorCostItems
    .filter(c => isBuydownPassThrough({
      category: 'investor_cost',
      line_type: c.lineType,
      description: c.description
    }))
    .reduce((sum, c) => sum + c.amount, 0);
  const otherInvestorCosts = totalInvestorCosts - investorInterestCost - escrowPassThrough - governmentFundingPassThrough - buydownPassThrough;
  const totalLenderCost = lenderCredit + cureAppliedLenderCredit + otherLenderCost;
  const fhaUfmipCompanyVariance = fhaUfmip?.companyVarianceAmount || 0;

  // --- Net Profit ---
  const netProfit = companyRevenue
    + interestIncome
    + servicingInterestIncome
    - warehouseInterest
    - otherWarehouseCosts
    - investorInterestCost
    - otherInvestorCosts
    - totalLenderCost
    + flexcloseCredit
    - totalOverrideAmount
    - fhaUfmipCompanyVariance;

  // --- Display totals ---
  const totalRevenueForDisplay = actualPurchaseDollar + processingFee + underwritingFee
    + interestIncome + servicingInterestIncome + nonReimbursableUserFees;
  const totalCostsForDisplay = totalLoCompensation + totalWarehouseCosts
    - shortageFunded - flexcloseCredit + (totalInvestorCosts - escrowPassThrough - governmentFundingPassThrough - buydownPassThrough)
    + totalLenderCost + totalOverrideAmount + fhaUfmipCompanyVariance;

  return {
    loPayoutBase,
    totalDeductions,
    loPayout,
    totalReimbursable,
    loPayoutWithReimbursables,
    volumeBonusAmount,
    totalLoCompensation,
    actualPurchaseDollar,
    gosAmount,
    processingFee,
    underwritingFee,
    nonReimbursableUserFees,
    companyRevenue,
    totalOverrideAmount,
    interestIncome,
    servicingInterestIncome,
    warehouseInterest,
    otherWarehouseCosts,
    shortageFunded,
    investorInterestCost,
    otherInvestorCosts,
    escrowPassThrough,
    governmentFundingPassThrough,
    buydownPassThrough,
    fhaUfmip,
    fhaUfmipCompanyVariance,
    lenderCredit,
    cureAppliedLenderCredit,
    otherLenderCost,
    totalLenderCost,
    flexcloseCredit,
    reimbursableItems,
    loDeductionItems,
    nonReimbursableUserFeeItems,
    investorCostItems,
    warehouseCostItems,
    lenderCreditItems,
    otherLenderCostItems,
    manualPassThroughItems,
    totalRevenueForDisplay,
    totalCostsForDisplay,
    netProfit
  };
}
